import { FC, memo, MouseEvent, useCallback } from 'react';
import { ButtonProps } from 'antd';
import { PopconfirmProps } from '../../../components';
import { useRemoveFormTableRow } from './useRemoveFormTableRow';
import { CellContext } from '@tanstack/react-table';
import { DeleteTableRowButton } from './DeleteTableRowButton';
import { useAdditionalFormContext } from '../../AdditionalFormContext';

export interface DeleteFormTableRowButtonProps extends Omit<ButtonProps, 'id'> {
  id?: string;
  context: CellContext<any, any>;
  confirm?:
    | boolean
    | (Partial<PopconfirmProps> &
        (
          | {
              isOpen: boolean;
              onOpenChange: (isOpen: boolean) => void;
            }
          | {
              isOpen?: never;
              onOpenChange?: never;
            }
        ));
}

export const DeleteFormTableRowButton: FC<DeleteFormTableRowButtonProps> = memo(
  (props) => {
    const { context, onClick, confirm = true, ...rest } = props;

    const removeRow = useRemoveFormTableRow();
    const { onChange: formOnChange } = useAdditionalFormContext();

    const deleteRow = useCallback(() => {
      removeRow(context.row.index);
    }, [removeRow, context.row.index]);

    const handleClick = useCallback(
      (event: MouseEvent<HTMLElement>) => {
        if (!confirm) {
          deleteRow();
          formOnChange?.();
        }
        onClick?.(event);
      },
      [deleteRow, formOnChange, confirm, onClick],
    );

    const handleConfirm = useCallback(
      (event: MouseEvent<HTMLElement>) => {
        deleteRow();
        formOnChange?.();
        if (typeof confirm === 'object' && 'onConfirm' in confirm) {
          confirm.onConfirm?.(event);
        }
      },
      [deleteRow, formOnChange, confirm],
    );

    return (
      <DeleteTableRowButton
        id={`delete-table-row-${context.row.index}`}
        confirm={
          confirm
            ? {
                ...(typeof confirm === 'object' && confirm),
                onConfirm: handleConfirm,
              }
            : undefined
        }
        onClick={handleClick}
        {...rest}
      />
    );
  },
);
